import { Target as HarnessServerTarget } from '@harnessio/ff-nodejs-server-sdk';

import { ISecurityGroup } from './security-group.interface';

export interface UserContextAttributes {
  userId?: string;
  displayName?: string;
  email?: string;
  tenantId?: string;
  tenantName?: string;
  tenantDomain?: string;
  packageId?: string;
  packageName?: string;
  packageAddonNames?: string[];
  packageAddonIds?: string[];
  groups?: ISecurityGroup[] | string;
  service?: string;
}

/**
 * Targets controls which users see which variation of a feature flag.
 * A target can be a userID, tenantID, application or a system
 *
 * Note: Targets can be both configured using the Harness Target Management page or directly
 * into the application by adding a target and its attributes via SDK.
 *
 * If a target is added via SDK, the Harness platform should
 * discover and populate it in the Harness Target Management page automatically.
 */
export type FeatureFlagServerTarget = HarnessServerTarget & UserContextAttributes;

/** This FeatureFlag enum should contain the list of feature flag keys defined in Harness Platform
 * Please make sure the flag key created in the Harness platform matches with the keys defined here.
 **/
export enum FeatureFlagCollection {
  userFlows = 'user_flows',
  userFlowsGuided = 'user_flows_guided',
  userFlowsQ42023Revamp = 'user_flows_q4_2023_revamp',
  workspaceMigration = 'workspace_migration',
  workspaceMigrationResync = 'workspace_migration_resync',
  scansListRedesign = 'scans_list_redesign',
  scansRevampInPopup = 'scans_revamp_in_pop_up',
  salesforceIdLookup = 'salesforce_id_lookup',
  requestValidationTesting = 'request_validation_testing',
  automaticTaskValidation = 'Automatic_Task_Validation',
  dpRiskScore = 'dp_risk_score',
  wsRiskScore = 'ws_risk_score',
  hideRiskManagementStatus = 'hide_risk_management_status',
  govDashWebsiteAppsNewSection = 'gov_dash_websiteapps_new_section',
  daFindingsMobileEnabled = 'DA_Findings_Mobile_Enabled',
  createLegacyEA = 'Create_LegacyEA_Tenant_LapDev',
  tenantRiskScore = 'tenant_risk_score',
  permissionLogging = 'permission_logging',
  aiChat = 'AI_chat',
  evaluationCopy = 'evaluation_copy',
  filteringUsers = 'filtering_users',
  scanBanner = 'scan_banner',
  newPackageMonitoringLimits = 'new_package_monitoring_limits',
  auditOnlyPackage = 'audit_only_package',
  bulkDpWithCustomMonitoringSettings = 'bulk_dp_with_custom_monitoring_settings',
  bulkWsAndDpsCreationWithCustomMonitoringSettings = 'bulk_ws_and_dps_creation_with_custom_monitoring_settings',
  pdfRemediationTestCount = 'pdf_remediation_test_count',
  eap_22459 = 'EAP22459',
  issueTrackingRedirect = 'issue_tracking_redirect',
  scimFunctionality = 'SCIM_functionality',
  scimGroupsSupport = 'scim_groups_support',
  removedStepper = 'removed_stepper',
  riskChartFrequency = 'risk_chart_frequency',
  organizationPortfolio = 'organization_portfolio',
  programPortfolioAssets = 'program_portfolio_assets',
  monitoringHistoricalReport = 'monitoring_historical_report',
  pushManualRequestAudit = 'push_manual_request_audit',
  evaluationsSummarization = 'evaluations_summarization',
  requestManualEvaluation = 'request_manual_evaluation',
  dcaDownload = 'dca_download',
  manualEvaluationConformanceTab = 'manual_evaluation_comformance_tab',
  conformanceScoreTrend = 'conformance_score_trend',
  manualEvaluationsSwa = 'Manual_Evaluations_SWA',
  asyncAiTesting = 'async_ai_testing',
  asyncAiTestingV2 = 'async_ai_testing_v2',
  userway_integration = 'userway_integration',
  platformWidgetMgmt = 'platform_widget_mgmt',
  platformWidgetLSTBtns = 'platform_widget_lst_btns',
  platformWidgetLean = 'platform_widget_lean',
  platformWidgetImgAlts = 'platform_widget_img_alts',
  platformWidgetTabHidden = 'platform_widget_tab_hidden',
  updateTaskSlideout = 'update_task_slideout',
  mobileTestingUseSauceLabs = 'mobile_testing_use_saucelabs',
  mobileAllowMultipleApps = 'mobile_allow_multiple_apps',
  navSupportPortalUx = 'nav_support_portal_ux',
  requestLiveConsultation = 'request_live_consultation',
  scanWithRobotstxt = 'SCAN_With_RobotTXT',
  labeVT = 'labe_VT',
  scanningAgent = 'scanning_agent',
  scanningMicroservice = 'scanning_microservice',
  addWaitTimeAutoScan = 'add_wait_time_auto_scan',
  commonFindings = 'common_findings',
  viewIssueTrackingConnectionStatus = 'View_Issue_Tracking_Connection_Status',
  manageIssueTrackingConnectionInProjects = 'Manage_Issue_Tracking_Connection_in_Projects',
  artificialIntelligenceOptin = 'artificial_intelligence_optin',
  digitalAssetFindings = 'Digital_asset_findings',
  digitalPropertyTagging = 'digital_property_tagging',
  asanaProjectsPagination = 'Asana_Projects_Pagination',
  artInScansAndMonitoring = 'ART_in_Scans_and_Monitoring',
  newAutomatedEngine = 'new_automated_engine',
  aiMonitoringSummary = 'AI_Monitoring_Summary',
  generateCommonFindingsForTenant = 'generate_common_findings_for_tenant',
  defaultCommonFindingsOptedIn = 'CF_default_opted_in',
  commonFindingsOptInTenant = 'CF_Opt_In_Tenant',
  generateFindingsAnalyticsForTenant = 'generate_findings_analytics_for_tenant',
  scanDebugging = 'Scan_debugging',
  dataEnrichmentMicroserviceIntegration = 'Project_impact',
  showCommonFindingsSettingsModal = 'show_common_findings_settings_modal',
  webhookHmacAuth = 'HMAC_Webhook_Auth',
  showStandardSelectPrompt = 'Evaluations_manual_standard_selection',
  enableLevelCI = 'enable_level_ci',
  verticalNavigation = 'vertical_navigation',
  verticalNavigationOptin = 'vertical_navigation_optin',
  verticalNavEnhancements = 'vertical_nav_enhancements',
  moveNavToggle = 'move_nav_toggle',
  editManualEvaluationStandards = 'edit_manual_evaluation_standards',
  expandCommonFindingsPackages = 'Expand_CF_Packages',
  commonFindingsBasicPackageAddon = 'Basic_CF_AddOn',
  digitalAssetManualFindings = 'DA_Findings_Manual_Findings',
  manualEvaExportNewFields = 'manual_eva_export_new__fields',
  brandRefresh = 'brand_refresh',
  selfManageOrgHierarchy = 'self_manage_org_hierarchy',
  atlasUserManagement = 'atlas_user_management',
  aiFiltering = 'AI_Filtering',
  JiraDC_OAuth2_UIFlow = 'JiraDC_OAuth2_UIFlow',
  OAuth2_Enhancements = 'OAuth2_Enhancements',
  AddonReportingProgramView = 'Add_on_Reporting_Program_View',
  AddonReportingEssentials = 'Add_on_Reporting_Essentials',
  aIPoweredManualEvaluations = 'AI_Powered_Manual_Evaluations',
  taSearchTestsV2 = 'TA_search_tests_v2',
  cuetEvaluations = 'cuet_evaluations',
  reportingAgentShareReport = 'reporting_agent_share_report',
  OAuth2_Reauthorize = 'OAuth2_Reauthorize',
  editPagesAndComponentsInMECreationFlow = 'Edit_pages_and_components_in_ME_creation_flow',
  aiPrioritizationRankingUI = 'AI_Prioritization_Ranking_UI',
  createAutoVpat = 'Create_auto_vpat',
  labe_page_search = 'labe_page_search',
  autoVpatTemplateDifferentiation = 'auto_vpat_template_differentiation',
  vpatPreviewCriteriaDistributionTable = 'vpat_preview_criteria_distribution_table',
  digitalAssetsPrivacySetting = 'Digital_Assets_Privacy_Setting',
  scanningAgentDebugMode = 'scanning_agent_debug_mode',
  automateTasks = 'Automate_Tasks',
  JiraDCCustomHeaders = 'JiraDC_CustomHeaders',
  laPdfValidationService = 'la_pdf_validation_service',
  laPdfAutoRemediation = 'la_pdf_auto_remediation',
  bulkAutoRemediatePdf = 'Bulk_Auto_Remediate_PDF',
  removeAddDAButton = 'remove_add_DA_button',
  showInternalNotesHideDescription = 'Show_Internal_Notes_Hide_Description',
  ActivateFigmaPluginUserRole = 'Activate_Figma_Plugin_User_Role',
  customReportsPage = 'custom_reports_page',
  knowledgeBaseMcp = 'knowledge_base_mcp',
  mcpOauth = 'mcp_oauth',
  apiProjects = 'api_projects',
  apiWorkspaceUsers = 'api_workspace_users',
  fixFindingDetails = 'fix_finding_details',
  preservePackageUsageLimits = 'preserve_package_usage_limits',
  scoreDesignSystem = 'score_design_system',
  ruleFilterDesignSystem = 'rule_filter_design_system',
  FigmaPlugin_PublishNewPlugin = 'FigmaPlugin_PublishNewPlugin',
  reportingAgentExport = 'reporting_agent_export',
  userSettingsModal = 'user_settings_modal',
  moveButtonsToHeaders = 'move_buttons_to_headers',
  recentScansTab = 'recent_scans_tab',
  addFilterTotalCount = 'CF_Add_Filter_Total_Count',
  labeAiFindingContentGeneration = 'labe_ai_finding_content_generation',
  reportingAgentStandardsFilter = 'reporting_agent_standards_filter',
  makeAcademyOptional = 'make_academy_optional',
  aiChatStreaming = 'ai_chat_streaming',
  aiChatAddContext = 'ai_chat_add_context',
  globalOrgSettings = 'global_org_settings',
  customApiTokenNotification = 'custom_api_token_notification',
  moveFilesMenu = 'move_files_menu',
  figmaAnnotationsMenu = 'FigmaPlugin_Annotations_Menu',
  figmaHeadingsOrder = 'Figma_Plugin_Headings_order',
  figmaAnnotationsAltText = 'FigmaPlugin_Annotations_AltText',
  figmaPluginAccessibilityNotes = 'Figma_plugin_accessibility_notes',
  figmaPluginAdditionalAccessibilityNotes = 'Figma_Plugin_Additional_Accessibility_Notes',
  tenantDecommission = 'tenant_decommission',
  customReportsTimeframePicker = 'custom_reports_timeframe_picker',
  flawMatchingMobile = 'flaw_matching_mobile',
  mobileEngineSdk = 'mobile_engine_sdk',
  digitalAssetFormUpdate = 'digital_asset_form_update',
  removeToReviewFromClientView = 'Remove_To_Review_from_Client_view',
  findingsDetailsRefresh = 'findings_details_refresh',
  stopGenerateReport = 'stop_generate_report',
  restoreReport = 'restore_report',
  apiTasks = 'api_tasks',
  reportingAgentChatStatus = 'reporting_agent_chat_status',
  dsTable = 'ds_table',
  dsTablePages = 'ds_table_pages',
  dsTableDesignReviewFindingsByScreen = 'ds_table_design_review_findings_by_screen',
  dsTableNotificationsList = 'ds_table_notifications_list',
  digitalAssetActivityVisibility = 'digital_asset_activity_visibility',
  dsTableDesignReviewFindingsByCategory = 'ds_table_design_review_findings_by_category',
  dsTableEvaluationRules = 'ds_table_evaluation_rules',
  dsTableAdminGlobalUsers = 'ds_table_admin_global_users',
  dsTableAdminInternalUsers = 'ds_table_admin_internal_users',
  dsTableMonitoringPages = 'ds_table_monitoring_pages',
  dsTableRemainingFindings = 'ds_table_remaining_findings',
  dsTableAdminGroups = 'ds_table_admin_groups',
  figmaPluginAiGeneratedAccessibilityNotes = 'Figma_Plugin_AI_Generated_Accessibility_Notes',
  figmaPluginAiContrastRecommendations = 'figma_plugin_ai_contrast_recommendations',
  figmaPluginAiAnnotationReview = 'Figma_Plugin_AI_Annotation_Review',
  figmaPluginAiAltTextAnnotations = 'FigmaPlugin_AutomateAnnotations_AltText',
  changeHowManualEvalsAreManagedByClients = 'Change_how_manual_evals_are_managed_by_clients',
  consolidateSettings = 'consolidate_settings',
  updateConformanceScoresForPartiallyDeliveredEvals = 'update_conformance_scores_for_partially_delivered_evals',
  scheduleReport = 'schedule_report',
  scheduleFlowEnhancement = 'schedule_flow_enhancement',
  githubIssues = 'github_issues',
  standardizeHeaderBars = 'standardize_header_bars',
  artMetricsSynchronization = 'art_metrics_synchronization',
  aiChatRenameChatSession = 'ai_chat_rename_chat_session',
  aiChatCombinedChatPanel = 'ai_chat_combined_chat_panel',
  aiChatUxImprovements = 'ai_chat_UX_improvements',
  duplicateReport = 'Duplicate_report',
  dsPageMessage = 'ds_page_message',
  dsDatepicker = 'ds_datepicker',
  githubIssuesBidirectional = 'GitHub_issues_Bidirectional',
  githubLinkExistingIssues = 'GitHub_LinkExistingIssues',
  githubCustomFields = 'GitHub_Custom_Fields',
  githubBulkPushOptions = 'GitHub_BulkPushOptions',
  gitlabIntegration = 'GitLab_Integration',
  manualTaskTracking = 'Manual_Task_Tracking',
  projectTaskViewTableFilterSortExport = 'ProjectTask_View_Table_FilterSortExport',
  defaultScanTimeout = 'default_scan_timeout',
  findingsWorkflowAndEditing = 'Findings_workflow_and_editing',
  findingsTaskLinkedComments = 'Findings_Task_Linked_Comments',
  labeBulkActions = 'LABE_bulk_actions',
  labeDeepLink = 'LABE_deep_link',
  aiChatPromptInputChange = 'ai_chat_prompt_input_change',
  zendeskApiOauth = 'Zendesk_API_OAuth',
  scoping = 'Scoping',
  mcpManualEvaluationsPermission = 'mcp_manual_evaluations_permission',
  userManagementRefresh = 'user_management_refresh',
  scanLevelMetadata = 'scan_level_metadata',
  exportTestedRulesTab = 'export_tested_rules_tab',
  separatedBacklogReopenedStatus = 'Separated_Backlog_Reopened_status',
  amplitudeGAndSSDK = 'amplitude_G_and_S_SDK',
  monitoringDocumentDiscovery = 'monitoring_document_discovery',
  allowEditTaskFindingsComments = 'Allow_Edit_Task_Findings_Comments',
  unlinkTaskHistory = 'Unlink_Task_History',
  inclineGoldenDataset = 'incline_golden_dataset',
}

export function revertFlag(flag: FeatureFlagCollection): string {
  return `!${flag}`;
}

// This is for FME and not FF
export enum FeatureFlagName {
  odt2 = 'odt2',
  enableZtEarlyAccess = 'enable_zt_early_access',
  mobileManualEvaluations = 'mobile_manual_evaluations',
}

/** Fallback tenantId FME attribute used when there is no current tenant context (e.g. superadmin, anonymous). */
export const FME_ADMIN_TENANT_ID: string = 'admin';

/** Fallback FME identity key used when there is no current user (e.g. pre-authentication, unauthenticated routes). */
export const FME_ANONYMOUS_KEY: string = 'anonymous';

/** Fallback treatment used when the FME client isn't configured/ready and the flag has no entry in {@link FME_FALLBACK_TREATMENT_BY_FLAG}. */
export const FME_DEFAULT_FALLBACK_TREATMENT: string = 'off';

/** Per-flag overrides of {@link FME_DEFAULT_FALLBACK_TREATMENT}, consulted by both the client and server FME services. */
export const FME_FALLBACK_TREATMENT_BY_FLAG: Readonly<Partial<Record<FeatureFlagName, string>>> = Object.freeze({});
